import SchemaFormAutoSubmit from "@/components/Schema/SchemaFormAutoSubmit";
import { PageHeader, ProColumns } from "@ant-design/pro-components";
import { createForm } from "@formily/core";
import { Button, Tabs, Popconfirm } from "antd";
import { useMemo, useRef, useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import MMNavigationRoutes from "../../utils/navigationRoutes";
import { Edit2, Trash, Plus, ArrowLeft } from "lucide-react";
import { DynamicModuleService } from "@/services/APIs";
import { notify } from "@/components/common/Notification";
import ConfigDynamicTable from "@/components/BaseTable/ConfigDynamicTable";
import CustomButton from "@/components/common/Button";
import WorkflowStatus from "@/components/WorkflowStatus";
import { useQuery } from "@tanstack/react-query";
import { CustomImageUpload } from "@/components/CustomImageUpload";
import CommonModal from "@/components/common/CommonModal";
import MemberService from "@/services/APIs/MemberService";

const ChapterProfilePage: React.FC = () => {
  const navigate = useNavigate();
  const basicInfoSchema = "MM Organization";
  const historyMilestonesSchema = "mm_history_milestones";
  const membershipTypesSchema = "MM membership type";
  const noticeBoardSchema = "MM Notice";
  const contactLocationsSchema = "mm_contacta_locations";
  const committeeLeadershipSchema = "Assign Chapter";
  const addcommitteeMember = "committee role";
  const documentsSchema = "mm_documents";
  const hoursstatus = "hours status";
  const photoGallerySchema = "mm_photo_gallery";
  const addphotogallery = "Add Photo Gallery";
  const bankingSchema = "Banking";
  const faqSchema = "mm_faq";
  const sponsorshipSchema = "MM Sponsor";
  const [searchParams] = useSearchParams();

  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  // const [editingRecord, setEditingRecord] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Individual form states for each schema
  const [editingSchema, setEditingSchema] = useState<string>("");
  const [editingRecordId, setEditingRecordId] = useState<string>("");

  // State to control add photo gallery form visibility
  const [showAddPhotoGallery, setShowAddPhotoGallery] =
    useState<boolean>(false);
  // State to track which photo name is being edited
  const [selectedPhotoName, setSelectedPhotoName] = useState<string>("");
  // State to control add member form visibility for committee
  const [showAddMember, setShowAddMember] = useState<boolean>(false);
  // State to control add member modal visibility
  const [showAddMemberModal, setShowAddMemberModal] = useState<boolean>(false);
  // State to track selected committee for showing member table
  const [selectedCommittee, setSelectedCommittee] = useState<any>(null);

  // Individual forms for each tab
  const formOverview = useMemo(() => createForm({}), []);
  const formHistoryMilestones = useMemo(() => createForm({}), []);
  const formMembershipTypes = useMemo(() => createForm({}), []);
  const formContactLocations = useMemo(() => createForm({}), []);
  const formHoursStatus = useMemo(() => createForm({}), []);
  const formPhotoGallery = useMemo(() => createForm({}), []);
  const formAddPhotoGallery = useMemo(() => createForm({}), []);
  const formAddCommitteeMember = useMemo(() => createForm({}), []);
  const formCommitteeLeadership = useMemo(() => createForm({}), []);
  const formNoticeBoard = useMemo(() => createForm({}), []);
  const formBanking = useMemo(() => createForm({}), []);
  const formDocuments = useMemo(() => createForm({}), []);
  const formFAQ = useMemo(() => createForm({}), []);
  const formSponsorship = useMemo(() => createForm({}), []);

  // Individual form refs for each tab
  const formOverviewRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formHistoryMilestonesRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formContactLocationsRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formHoursStatusRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formPhotoGalleryRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formAddPhotoGalleryRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formAddCommitteeMemberRef = useRef<{ handleSave: () => Promise<void> }>(
    null
  );
  const formCommitteeLeadershipRef = useRef<{
    handleSave: () => Promise<void>;
  }>(null);
  const formNoticeBoardRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formBankingRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formDocumentsRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formFAQRef = useRef<{ handleSave: () => Promise<void> }>(null);
  const formSponsorshipRef = useRef<{ handleSave: () => Promise<void> }>(null);

  const emailFromUrl = searchParams.get("email");

  // Alternative: Use a callback to get the created ID
  const handleSaveOrganization = async () => {
    try {
      // Validate form first
      await formOverview.validate();
      const formValues = formOverview.values;

      const response = await DynamicModuleService.createItem(
        basicInfoSchema ?? "",
        formValues
      );

      if (response?.id) {
        navigate("/mm" + MMNavigationRoutes.EDIT_CHAPTER_PROFILE(response.id));
        // notify.success("Organization created successfully");
      } else {
        notify.error("Failed to get organization ID after creation");
      }
    } catch (error) {
      console.error("Save error:", error);
      // notify.error("Please fill in all required fields correctly");
    }
  };

  if (isEditMode) {
    // Set initial values for all forms
    formOverview.setInitialValues({
      id: id,
      jdid: id,
      primaryemail: emailFromUrl ?? "",
    });

    formHistoryMilestones.setInitialValues({
      jdid: id,
    });

    formMembershipTypes.setInitialValues({
      jdid: id,
    });

    formContactLocations.setInitialValues({
      jdid: id,
    });

    formHoursStatus.setInitialValues({
      jdid: id,
    });

    formPhotoGallery.setInitialValues({
      jdid: id,
    });

    formAddPhotoGallery.setInitialValues({
      jdid: id,
    });

    formAddCommitteeMember.setInitialValues({
      jdid: id,
    });

    formCommitteeLeadership.setInitialValues({
      jdid: id,
    });

    formNoticeBoard.setInitialValues({
      jdid: id,
    });

    formBanking.setInitialValues({
      jdid: id,
    });

    formDocuments.setInitialValues({
      jdid: id,
    });

    formFAQ.setInitialValues({
      jdid: id,
    });

    formSponsorship.setInitialValues({
      jdid: id,
    });
  }

  // Handle edit item - Load data into form instead of navigating
  const handleEdit = (record: any, schemaName: string) => {
    setEditingSchema(schemaName);
    setEditingRecordId(record.id);
    setIsEditing(true);

    // Load the record data into the appropriate form based on schema
    const formData = {
      ...record,
      jdid: id, // Ensure jdid is set to the current chapter ID
    };

    const setupFormForEdit = (form: any) => {
      form.setInitialValues(formData);
      form.setValues(formData);
      form.clearErrors();
    };

    if (schemaName === historyMilestonesSchema) {
      setupFormForEdit(formHistoryMilestones);
    } else if (schemaName === membershipTypesSchema) {
      setupFormForEdit(formMembershipTypes);
    } else if (schemaName === contactLocationsSchema) {
      setupFormForEdit(formContactLocations);
    } else if (schemaName === hoursstatus) {
      setupFormForEdit(formHoursStatus);
    } else if (schemaName === photoGallerySchema) {
      setupFormForEdit(formPhotoGallery);
    } else if (schemaName === addphotogallery) {
      setupFormForEdit(formAddPhotoGallery);
    } else if (schemaName === committeeLeadershipSchema) {
      setupFormForEdit(formCommitteeLeadership);
    } else if (schemaName === noticeBoardSchema) {
      setupFormForEdit(formNoticeBoard);
    } else if (schemaName === bankingSchema) {
      setupFormForEdit(formBanking);
    } else if (schemaName === documentsSchema) {
      setupFormForEdit(formDocuments);
    } else if (schemaName === faqSchema) {
      setupFormForEdit(formFAQ);
    } else if (schemaName === sponsorshipSchema) {
      setupFormForEdit(formSponsorship);
    }
  };

  // Cancel editing and reset form
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditingSchema("");
    setEditingRecordId("");
    // Reset appropriate form to initial values
    if (editingSchema === historyMilestonesSchema) {
      resetFormHistoryMilestones();
    } else if (editingSchema === membershipTypesSchema) {
      resetFormMembershipTypes();
    } else if (editingSchema === contactLocationsSchema) {
      resetFormContactLocations();
    } else if (editingSchema === hoursstatus) {
      resetFormHoursStatus();
    } else if (editingSchema === photoGallerySchema) {
      resetFormPhotoGallery();
    } else if (editingSchema === addphotogallery) {
      resetFormAddPhotoGallery();
    } else if (editingSchema === committeeLeadershipSchema) {
      resetFormCommitteeLeadership();
    } else if (editingSchema === noticeBoardSchema) {
      resetFormNoticeBoard();
    } else if (editingSchema === bankingSchema) {
      resetFormBanking();
    } else if (editingSchema === documentsSchema) {
      resetFormDocuments();
    } else if (editingSchema === faqSchema) {
      resetFormFAQ();
    } else if (editingSchema === sponsorshipSchema) {
      resetFormSponsorship();
    }
  };

  // Reset the contact locations form to initial state
  const resetFormContactLocations = () => {
    formContactLocations.reset();
    formContactLocations.setInitialValues({});
    formContactLocations.setValues({});
    if (id) {
      formContactLocations.setInitialValues({ jdid: id });
      formContactLocations.setValues({ jdid: id });
    }
    formContactLocations.clearErrors();
    formContactLocations.clearFormGraph();
  };

  // Reset the hours status form to initial state
  const resetFormHoursStatus = () => {
    formHoursStatus.reset();
    formHoursStatus.setInitialValues({});
    formHoursStatus.setValues({});
    if (id) {
      formHoursStatus.setInitialValues({ jdid: id });
      formHoursStatus.setValues({ jdid: id });
    }
    formHoursStatus.clearErrors();
    formHoursStatus.clearFormGraph();
  };

  // Reset the photo gallery form to initial state
  const resetFormPhotoGallery = () => {
    formPhotoGallery.reset();
    formPhotoGallery.setInitialValues({});
    formPhotoGallery.setValues({});
    if (id) {
      formPhotoGallery.setInitialValues({ jdid: id });
      formPhotoGallery.setValues({ jdid: id });
    }
    formPhotoGallery.clearErrors();
    formPhotoGallery.clearFormGraph();
  };

  // Reset the add photo gallery form to initial state
  const resetFormAddPhotoGallery = () => {
    formAddPhotoGallery.reset();
    formAddPhotoGallery.setInitialValues({});
    formAddPhotoGallery.setValues({});
    if (id) {
      formAddPhotoGallery.setInitialValues({ jdid: id });
      formAddPhotoGallery.setValues({ jdid: id });
    }
    formAddPhotoGallery.clearErrors();
    formAddPhotoGallery.clearFormGraph();
  };

  // Reset the committee leadership form to initial state
  const resetFormCommitteeLeadership = () => {
    formCommitteeLeadership.reset();
    formCommitteeLeadership.setInitialValues({});
    formCommitteeLeadership.setValues({});
    if (id) {
      formCommitteeLeadership.setInitialValues({ jdid: id });
      formCommitteeLeadership.setValues({ jdid: id });
    }
    formCommitteeLeadership.clearErrors();
    formCommitteeLeadership.clearFormGraph();
  };

  // Reset the notice board form to initial state
  const resetFormNoticeBoard = () => {
    formNoticeBoard.reset();
    formNoticeBoard.setInitialValues({});
    formNoticeBoard.setValues({});
    if (id) {
      formNoticeBoard.setInitialValues({ jdid: id });
      formNoticeBoard.setValues({ jdid: id });
    }
    formNoticeBoard.clearErrors();
    formNoticeBoard.clearFormGraph();
  };

  // Reset the documents form to initial state
  const resetFormDocuments = () => {
    formDocuments.reset();
    formDocuments.setInitialValues({});
    formDocuments.setValues({});
    if (id) {
      formDocuments.setInitialValues({ jdid: id });
      formDocuments.setValues({ jdid: id });
    }
    formDocuments.clearErrors();
    formDocuments.clearFormGraph();
  };

  // Reset the FAQ form to initial state
  const resetFormFAQ = () => {
    formFAQ.reset();
    formFAQ.setInitialValues({});
    formFAQ.setValues({});
    if (id) {
      formFAQ.setInitialValues({ jdid: id });
      formFAQ.setValues({ jdid: id });
    }
    formFAQ.clearErrors();
    formFAQ.clearFormGraph();
  };

  // Reset the banking form to initial state
  const resetFormBanking = () => {
    formBanking.reset();
    formBanking.setInitialValues({});
    formBanking.setValues({});
    if (id) {
      formBanking.setInitialValues({ jdid: id });
      formBanking.setValues({ jdid: id });
    }
    formBanking.clearErrors();
    formBanking.clearFormGraph();
  };

  // Reset the history milestones form to initial state
  const resetFormHistoryMilestones = () => {
    formHistoryMilestones.reset();
    formHistoryMilestones.setInitialValues({});
    formHistoryMilestones.setValues({});
    if (id) {
      formHistoryMilestones.setInitialValues({ jdid: id });
      formHistoryMilestones.setValues({ jdid: id });
    }
    formHistoryMilestones.clearErrors();
    formHistoryMilestones.clearFormGraph();
  };

  // Reset the membership types form to initial state
  const resetFormMembershipTypes = () => {
    formMembershipTypes.reset();
    formMembershipTypes.setInitialValues({});
    formMembershipTypes.setValues({});
    if (id) {
      formMembershipTypes.setInitialValues({ jdid: id });
      formMembershipTypes.setValues({ jdid: id });
    }
    formMembershipTypes.clearErrors();
    formMembershipTypes.clearFormGraph();
  };

  // Reset the sponsorship form to initial state
  const resetFormSponsorship = () => {
    formSponsorship.reset();
    formSponsorship.setInitialValues({});
    formSponsorship.setValues({});
    if (id) {
      formSponsorship.setInitialValues({ jdid: id });
      formSponsorship.setValues({ jdid: id });
    }
    formSponsorship.clearErrors();
    formSponsorship.clearFormGraph();
  };

  // Reset the overview form to initial state
  // const resetFormOverview = () => {
  //   formOverview.reset();
  //   formOverview.setInitialValues({});
  //   formOverview.setValues({});
  //   if (id) {
  //     formOverview.setInitialValues({ id: id, jdid: id });
  //     formOverview.setValues({ id: id, jdid: id });
  //   }
  //   formOverview.clearErrors();
  //   formOverview.clearFormGraph();
  // };

  const [refreshKey, setRefreshKey] = useState(0);

  const refreshTableData = () => {
    setRefreshKey(prev => prev + 1);
  };

  // Fetch all members for dropdown
  const { data: membersData } = useQuery({
    queryKey: ["allMembers"],
    queryFn: async () => {
      const response = await MemberService.getAllMembers({
        page: 1,
        pageSize: 1000,
      });
      return response.results;
    },
    enabled: true,
  });

  // Filter members by organization_id and jdid
  const filteredMembersData = useMemo(() => {
    if (!membersData || !id) {
      return membersData;
    }

    return membersData.filter((member: any) => {
      // Check if member belongs to the same organization as the chapter
      return member.organization_id === id || member.jdid === id;
    });
  }, [membersData, id]);

  // Set dataSource for member field in the add committee member form
  useEffect(() => {
    if (filteredMembersData && formAddCommitteeMember && showAddMemberModal) {
      const memberOptions = filteredMembersData.map(member => ({
        label:
          `${member.member_first_name ?? ""} ${member.member_last_name ?? ""}`.trim() ||
          "-",
        value: member.member_id,
      }));

      // Common field names that might contain member selection
      const commonFieldNames = [
        "member",
        "member_id",
        "memberName",
        "member_name",
        "member_field",
        "memberId",
        "member_name",
        "memberName",
      ];

      // Create a member lookup map for quick access
      const memberLookupMap = new Map(
        filteredMembersData.map(member => [member.member_id, member])
      );

      // Set options and onChange handler for all possible member field names
      commonFieldNames.forEach(fieldName => {
        formAddCommitteeMember.setFieldState(fieldName, state => {
          if (state) {
            const existingOnChange = state.componentProps?.onChange;

            state.componentProps = {
              ...state.componentProps,
              options: memberOptions,
              onChange: (memberId: string) => {
                // Call existing onChange if it exists
                if (existingOnChange) {
                  existingOnChange(memberId);
                }

                // Find the selected member
                const selectedMember = memberLookupMap.get(memberId);
                console.log("Member selected - memberId:", memberId);
                console.log("Selected member data:", selectedMember);

                if (selectedMember) {
                  // Access position and email fields
                  const position = (selectedMember as any).position;
                  const email =
                    (selectedMember as any).member_email ??
                    (selectedMember as any).email;

                  console.log("Member selected - updates:", {
                    position,
                    email,
                  });

                  // Set multiple fields at once to avoid state conflicts
                  try {
                    formAddCommitteeMember.setValues(
                      {
                        position: position ?? "",
                        email: email ?? "",
                      },
                      "merge"
                    );
                    console.log("Form values updated successfully");
                  } catch (error) {
                    console.error(
                      "Error setting member details in form:",
                      error
                    );
                  }
                } else {
                  console.log("Selected member not found in lookup map");
                }
              },
            };
            state.dataSource = memberOptions;
          }
        });
      });
    }
  }, [filteredMembersData, formAddCommitteeMember, showAddMemberModal]);

  // Handle add committee member modal save
  const handleAddMemberModalSave = async () => {
    try {
      const formValues = formAddCommitteeMember.values;
      const jdid = formValues.jdid;

      if (id === jdid) {
        // Ensure cid is set if not already in form values
        if (selectedCommittee && !formValues.cid) {
          formAddCommitteeMember.setValues({
            ...formValues,
            cid: selectedCommittee.id,
          });
        }

        await formAddCommitteeMemberRef.current?.handleSave();
        refreshTableData();
        formAddCommitteeMember.reset();
        formAddCommitteeMember.clearErrors();
        formAddCommitteeMember.clearFormGraph();
        setShowAddMemberModal(false);
      } else {
        notify.error("Chapter ID mismatch! Please check the ID field.");
      }
    } catch (error) {
      console.error("Save error:", error);
    }
  };

  const handleDelete = async (id: string, schemaName: string) => {
    try {
      await DynamicModuleService.deleteItem(schemaName, id);
      refreshTableData();

      // Clear the form if the deleted record was being edited
      if (isEditing && editingSchema === schemaName && editingRecordId === id) {
        handleCancelEdit();
      }

      // Clear the appropriate form based on schema
      if (schemaName === historyMilestonesSchema) {
        resetFormHistoryMilestones();
      } else if (schemaName === membershipTypesSchema) {
        resetFormMembershipTypes();
      } else if (schemaName === contactLocationsSchema) {
        resetFormContactLocations();
      } else if (schemaName === hoursstatus) {
        resetFormHoursStatus();
      } else if (schemaName === photoGallerySchema) {
        resetFormPhotoGallery();
      } else if (schemaName === addphotogallery) {
        resetFormAddPhotoGallery();
      } else if (schemaName === committeeLeadershipSchema) {
        resetFormCommitteeLeadership();
      } else if (schemaName === noticeBoardSchema) {
        resetFormNoticeBoard();
      } else if (schemaName === bankingSchema) {
        resetFormBanking();
      } else if (schemaName === documentsSchema) {
        resetFormDocuments();
      } else if (schemaName === faqSchema) {
        resetFormFAQ();
      } else if (schemaName === sponsorshipSchema) {
        resetFormSponsorship();
      }

      // notify.success("Record deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      notify.error("Failed to delete record");
    }
  };

  /**
   * Custom component for rendering membership types as cards
   */
  // const MembershipTypesCard: React.FC = () => {
  //   const {
  //     data: membershipData,
  //     isLoading,
  //     refetch,
  //   } = useQuery({
  //     queryKey: ["membershipTypes", membershipTypesSchema, id, refreshKey],
  //     queryFn: () =>
  //       DynamicModuleService.getGridCoByConfig(
  //         membershipTypesSchema || "",
  //         1,
  //         100, // Get more records for card view
  //         [
  //           "id",
  //           "membershipname",
  //           "baseprice",
  //           "benefits",
  //           "duration",
  //           "is_active",
  //         ], // Include benefits and duration fields
  //         {
  //           jdid: id, // Use the chapter ID from URL params for filtering
  //         }
  //       ),
  //     enabled: !!membershipTypesSchema && !!id,
  //   });

  //   // Refresh data when refreshKey changes
  //   useEffect(() => {
  //     if (refreshKey > 0) {
  //       refetch();
  //     }
  //   }, [refetch]);

  //   if (isLoading) {
  //     return <div>Loading membership types...</div>;
  //   }

  //   const membershipTypes = membershipData ?? [];

  //   return (
  //     <div style={{ marginTop: "24px" }}>
  //       <h3
  //         style={{ marginBottom: "16px", fontSize: "18px", fontWeight: "bold" }}
  //       >
  //         Membership Types
  //       </h3>
  //       {membershipTypes.length === 0 ? (
  //         <div
  //           style={{
  //             textAlign: "center",
  //             padding: "40px",
  //             color: "#999",
  //             fontSize: "16px",
  //           }}
  //         >
  //           No membership types found. Add a new membership type using the form
  //           above.
  //         </div>
  //       ) : (
  //         <div
  //           style={{
  //             display: "grid",
  //             gridTemplateColumns: "repeat(auto-fit, 350px)",
  //             gap: "20px",
  //             padding: "0 8px",
  //           }}
  //         >
  //           {membershipTypes.map((membership: any) => (
  //             <div
  //               key={membership.id}
  //               style={{
  //                 borderRadius: 20,
  //                 background: "#fff",
  //                 width: "350px",
  //                 height: "194px",
  //                 boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  //                 position: "relative",
  //                 cursor: "pointer",
  //                 display: "flex",
  //                 flexDirection: "column",
  //               }}
  //             >
  //               {/* Header Section */}
  //               <div
  //                 style={{
  //                   padding: "20px",
  //                   position: "relative",
  //                   display: "flex",
  //                   justifyContent: "space-between",
  //                   alignItems: "center",
  //                   background: "#fff",
  //                   flex: "0 0 auto",
  //                   borderRadius: "20px 20px 0 0",
  //                 }}
  //               >
  //                 <div
  //                   style={{
  //                     fontSize: "20px",
  //                     fontWeight: "bold",
  //                     color: "#000",
  //                   }}
  //                 >
  //                   {membership.membershipname ?? "Membership"}
  //                 </div>
  //                 <div
  //                   style={{
  //                     display: "flex",
  //                     alignItems: "center",
  //                     gap: "8px",
  //                   }}
  //                 >
  //                   <ActiveToggleButton
  //                     table={membershipTypesSchema}
  //                     pk={membership.id}
  //                     is_active={membership.is_active}
  //                   />
  //                   <div
  //                     style={{
  //                       fontSize: "16px",
  //                       fontWeight: "600",
  //                       color: "#635DE8",
  //                       backgroundColor: "#f0f0ff",
  //                       padding: "6px 12px",
  //                       borderRadius: "20px",
  //                     }}
  //                   >
  //                     ${membership.baseprice ?? "0"}
  //                   </div>
  //                   {membership.duration && (
  //                     <div
  //                       style={{
  //                         fontSize: "14px",
  //                         fontWeight: "500",
  //                         color: "#666",
  //                         backgroundColor: "#f5f5f5",
  //                         padding: "4px 8px",
  //                         borderRadius: "12px",
  //                       }}
  //                     >
  //                       {membership.duration}
  //                     </div>
  //                   )}

  //                   <Button
  //                     type="text"
  //                     style={{
  //                       padding: "6px",
  //                       height: "auto",
  //                       minWidth: "auto",
  //                       backgroundColor: "#f0f0ff",
  //                       borderRadius: "8px",
  //                       border: "none",
  //                     }}
  //                     onClick={() =>
  //                       handleEdit(membership, membershipTypesSchema)
  //                     }
  //                     disabled={
  //                       isEditing && editingSchema !== membershipTypesSchema
  //                     }
  //                   >
  //                     <Edit2 size={16} color="#635DE8" />
  //                   </Button>
  //                 </div>
  //               </div>

  //               {/* Benefits Section */}
  //               <div
  //                 style={{
  //                   padding: "0 20px 20px 20px",
  //                   background: "#fff",
  //                   flex: "1 1 auto",
  //                   display: "flex",
  //                   flexDirection: "column",
  //                   borderRadius: "0 0 20px 20px",
  //                 }}
  //               >
  //                 <div
  //                   style={{
  //                     marginBottom: "12px",
  //                     color: "#635DE8",
  //                     fontSize: "16px",
  //                     fontWeight: "bold",
  //                   }}
  //                 >
  //                   Benefits
  //                 </div>
  //                 <ul
  //                   style={{
  //                     paddingLeft: "20px",
  //                     margin: 0,
  //                     color: "#4E4B6E",
  //                     lineHeight: "1.6",
  //                     fontSize: "14px",
  //                     flex: "1 1 auto",
  //                     listStyleType: "disc",
  //                     listStylePosition: "inside",
  //                   }}
  //                 >
  //                   {membership.benefits ? (
  //                     Array.isArray(membership.benefits) ? (
  //                       membership.benefits.map((benefit: string) => (
  //                         <li
  //                           key={`${membership.id}-benefit-${benefit.replace(/\s+/g, "-").toLowerCase()}`}
  //                           style={{ whiteSpace: "pre-line" }}
  //                         >
  //                           {benefit}
  //                         </li>
  //                       ))
  //                     ) : (
  //                       // Split by line breaks and create bullet points for each line
  //                       membership.benefits
  //                         .split("\n")
  //                         .filter((line: string) => line.trim() !== "")
  //                         .map((line: string) => (
  //                           <li
  //                             key={`${membership.id}-benefit-${line.trim().replace(/\s+/g, "-").toLowerCase()}`}
  //                             style={{ whiteSpace: "pre-line" }}
  //                           >
  //                             {line.trim()}
  //                           </li>
  //                         ))
  //                     )
  //                   ) : (
  //                     <li>Standard membership benefits included</li>
  //                   )}
  //                 </ul>
  //               </div>
  //             </div>
  //           ))}
  //         </div>
  //       )}
  //     </div>
  //   );
  // };

  const columnsmilestones: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Date",
      dataIndex: "date",
      render: (date: any) => {
        if (!date) {
          return "-";
        }
        const d = new Date(date);
        if (isNaN(d.getTime())) {
          return date;
        }
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      },
    },
    {
      title: "Milestone Name",
      dataIndex: "milestonename",
    },
    {
      title: "Description",
      dataIndex: "summary",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, historyMilestonesSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === historyMilestonesSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this milestone?"
          onConfirm={() => handleDelete(record.id, historyMilestonesSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === historyMilestonesSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === historyMilestonesSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnsnoticeboard: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Visibility",
      dataIndex: "visibilitys",
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, noticeBoardSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === noticeBoardSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this notice?"
          onConfirm={() => handleDelete(record.id, noticeBoardSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === noticeBoardSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === noticeBoardSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnsContact: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Title",
      dataIndex: "title",
    },
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Phone",
      dataIndex: "phone",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, contactLocationsSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === contactLocationsSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this contact?"
          onConfirm={() => handleDelete(record.id, contactLocationsSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === contactLocationsSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === contactLocationsSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnCommittee: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Committee Name",
      dataIndex: "committeename",
    },

    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="Add Member"
          icon={<Plus size={16} />}
          onClick={() => {
            setSelectedCommittee(record);
            setShowAddMember(true);
          }}
          type="text"
        />,
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, committeeLeadershipSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === committeeLeadershipSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this committee member?"
          onConfirm={() => handleDelete(record.id, committeeLeadershipSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === committeeLeadershipSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === committeeLeadershipSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnCommitteeMembers: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Member Name",
      dataIndex: "member",
      render: (_: any, record) => {
        return (
          <div>
            {
              filteredMembersData?.find(
                member => member.member_id === record.member
              )?.member_first_name
            }{" "}
            {
              filteredMembersData?.find(
                member => member.member_id === record.member
              )?.member_last_name
            }
          </div>
        );
      },
    },
    {
      title: "Role",
      dataIndex: "committeerole",
    },

    {
      title: "Position",
      dataIndex: "position",
    },
    {
      title: "Email",
      dataIndex: "email",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <Popconfirm
          title="Are you sure you want to delete this member?"
          onConfirm={() => handleDelete(record.id, addcommitteeMember)}
          okText="Yes"
          key="delete"
          cancelText="No"
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
          />
        </Popconfirm>,
      ],
    },
  ];

  const columndocuments: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "File Title",
      dataIndex: "filetitle",
    },
    {
      title: "Visibility",
      dataIndex: "visibility",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, documentsSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === documentsSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this document?"
          onConfirm={() => handleDelete(record.id, documentsSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === documentsSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === documentsSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnshoursstatus: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Description",
      dataIndex: "description",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, hoursstatus)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === hoursstatus &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this hours & status record?"
          onConfirm={() => handleDelete(record.id, hoursstatus)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === hoursstatus &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === hoursstatus &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnGallery: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Photo Name",
      dataIndex: "gallery",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="add"
          icon={<Plus size={16} />}
          onClick={() => {
            setSelectedPhotoName(record.gallery ?? "");
            setShowAddPhotoGallery(!showAddPhotoGallery);
          }}
          type="text"
          disabled={
            isEditing &&
            editingSchema === photoGallerySchema &&
            editingRecordId !== record.id
          }
        />,
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, photoGallerySchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === photoGallerySchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this gallery image?"
          onConfirm={() => handleDelete(record.id, photoGallerySchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === photoGallerySchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === photoGallerySchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnsbanking: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Payment Getaway Name",
      dataIndex: "payment",
    },
    {
      title: "Marchant Getaway Id",
      dataIndex: "marchantgetway",
    },
    {
      title: "Api Secret Key",
      dataIndex: "apisecretkey",
    },
    {
      title: "Webhook Url",
      dataIndex: "webhook",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, bankingSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === bankingSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this payment gateway?"
          onConfirm={() => handleDelete(record.id, bankingSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === bankingSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === bankingSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnsFAQ: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Questions",
      dataIndex: "questions",
    },
    {
      title: "Answers",
      dataIndex: "answer",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, faqSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === faqSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this FAQ?"
          onConfirm={() => handleDelete(record.id, faqSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === faqSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === faqSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const columnsSponsorship: ProColumns<any>[] = [
    {
      title: "ID",
      dataIndex: "id",
      hidden: true,
    },
    {
      title: "Sponsor Organization",
      dataIndex: "sponsororganization",
    },
    {
      title: "Actions",
      width: 100,
      valueType: "option",
      render: (_, record) => [
        <CustomButton
          key="edit"
          icon={<Edit2 size={16} />}
          onClick={() => handleEdit(record, sponsorshipSchema)}
          type="text"
          disabled={
            isEditing &&
            editingSchema === sponsorshipSchema &&
            editingRecordId !== record.id
          }
        />,
        <Popconfirm
          title="Are you sure you want to delete this sponsorship?"
          onConfirm={() => handleDelete(record.id, sponsorshipSchema)}
          okText="Yes"
          key="delete"
          cancelText="No"
          disabled={
            isEditing &&
            editingSchema === sponsorshipSchema &&
            editingRecordId !== record.id
          }
        >
          <CustomButton
            danger
            key="delete"
            icon={<Trash size={16} />}
            type="text"
            disabled={
              isEditing &&
              editingSchema === sponsorshipSchema &&
              editingRecordId !== record.id
            }
          />
        </Popconfirm>,
      ],
    },
  ];

  const tabLabelClassName = "text-sm text-foreground";
  // Generate title based on mode and fname from URL
  const getPageTitle = () => {
    return isEditMode ? "Edit Organization" : "Add Organization";
  };
  return (
    <>
      <PageHeader
        className="custom-page-header"
        title={getPageTitle()}
        onBack={() => navigate("/mm" + MMNavigationRoutes.CHAPTER_PROFILE_LIST)}
        extra={
          <WorkflowStatus
            schemaName={basicInfoSchema ?? ""}
            recordId={isEditMode ? (id ?? "") : ""}
          />
        }
      />

      <Tabs
        items={
          isEditMode
            ? [
                {
                  key: "1",
                  label: <span className={tabLabelClassName}>Basic</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        recordId={id}
                        ref={formOverviewRef}
                      />
                      <div className="flex justify-end gap-4">
                        <Button
                          type="primary"
                          onClick={async () => {
                            try {
                              await formOverviewRef.current?.handleSave();
                              refreshTableData();
                              // notify.success(
                              //   "Organization updated successfully"
                              // );
                            } catch (error) {
                              console.error("Update error:", error);
                              // Validation error message is already shown by SchemaFormAutoSubmit
                            }
                          }}
                        >
                          Update
                        </Button>
                      </div>
                    </>
                  ),
                },
                {
                  key: "2",
                  label: <span className={tabLabelClassName}>History</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formHistoryMilestones}
                        schemaName={historyMilestonesSchema || ""}
                        recordId={
                          isEditing && editingSchema === historyMilestonesSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formHistoryMilestonesRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === historyMilestonesSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues =
                                    formHistoryMilestones.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formHistoryMilestonesRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormHistoryMilestones();
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Milestone
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formHistoryMilestones.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formHistoryMilestonesRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormHistoryMilestones();
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Milestone
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        key={refreshKey}
                        columns={columnsmilestones}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Milestones`}
                        tableName={historyMilestonesSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
                {
                  key: "5",
                  label: <span className={tabLabelClassName}>Contact</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formContactLocations}
                        schemaName={contactLocationsSchema || ""}
                        recordId={
                          isEditing && editingSchema === contactLocationsSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formContactLocationsRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing &&
                        editingSchema === contactLocationsSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues =
                                    formContactLocations.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formContactLocationsRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormContactLocations();
                                    // notify.success(
                                    //   "Contact & location updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Contact & Location
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formContactLocations.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formContactLocationsRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormContactLocations();
                                  // notify.success(
                                  //   "Contact & location saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Contact & Location
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsContact}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Contact & Location`}
                        tableName={contactLocationsSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },

                {
                  key: "8",
                  label: <span className={tabLabelClassName}>Hours</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formHoursStatus}
                        schemaName={hoursstatus || ""}
                        recordId={
                          isEditing && editingSchema === hoursstatus
                            ? editingRecordId
                            : ""
                        }
                        ref={formHoursStatusRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === hoursstatus ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formHoursStatus.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formHoursStatusRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormHoursStatus();
                                    // notify.success(
                                    //   "Hours & status updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Hours & Status
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formHoursStatus.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formHoursStatusRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormHoursStatus();
                                  // notify.success(
                                  //   "Hours & status saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Hours & Status
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnshoursstatus}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Hours & Status`}
                        tableName={hoursstatus || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },

                {
                  key: "9",
                  label: <span className={tabLabelClassName}>Gallery</span>,
                  children: (
                    <>
                      {!showAddPhotoGallery && (
                        <SchemaFormAutoSubmit
                          form={formPhotoGallery}
                          schemaName={photoGallerySchema || ""}
                          recordId={
                            isEditing && editingSchema === photoGallerySchema
                              ? editingRecordId
                              : ""
                          }
                          ref={formPhotoGalleryRef}
                        />
                      )}
                      {!showAddPhotoGallery && (
                        <div className="flex justify-end gap-4">
                          {isEditing && editingSchema === photoGallerySchema ? (
                            <>
                              <Button
                                type="primary"
                                onClick={async () => {
                                  try {
                                    const formValues = formPhotoGallery.values;
                                    const jdid = formValues.jdid;

                                    if (id === jdid) {
                                      await formPhotoGalleryRef.current?.handleSave();
                                      handleCancelEdit();
                                      refreshTableData();
                                      resetFormPhotoGallery();
                                      // notify.success(
                                      //   "Gallery image updated successfully"
                                      // );
                                    } else {
                                      notify.error(
                                        "Chapter ID mismatch! Please check the ID field."
                                      );
                                    }
                                  } catch (error) {
                                    console.error("Save error:", error);
                                    // Validation error message is already shown by SchemaFormAutoSubmit
                                  }
                                }}
                              >
                                Update Gallery Image
                              </Button>
                              <Button onClick={handleCancelEdit}>Cancel</Button>
                            </>
                          ) : (
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formPhotoGallery.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formPhotoGalleryRef.current?.handleSave();
                                    refreshTableData();
                                    resetFormPhotoGallery();
                                    // notify.success(
                                    //   "Gallery image saved successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Save Gallery Image
                            </Button>
                          )}
                        </div>
                      )}

                      {showAddPhotoGallery && (
                        <>
                          {/* <SchemaFormAutoSubmit
                            form={formAddPhotoGallery}
                            schemaName={addphotogallery || ""}
                            recordId={
                              isEditing && editingSchema === addphotogallery
                                ? editingRecordId
                                : ""
                            }
                            ref={formAddPhotoGalleryRef}
                          /> */}
                          <h2>
                            Upload Image for: {selectedPhotoName || "Gallery"}
                          </h2>
                          <CustomImageUpload
                            multiple
                            value={id}
                            onUploaded={(response, file) => {
                              console.log(response, file, id);
                              const formValues = formAddPhotoGallery.values;
                              formAddPhotoGallery.setValues({
                                ...formValues,
                                images: [...formValues.images, id],
                              });
                              console.log([
                                ...formValues.images,
                                response.data[0].id,
                              ]);
                            }}
                          />
                          <div className="flex justify-end gap-4">
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formAddPhotoGallery.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formAddPhotoGalleryRef.current?.handleSave();
                                    refreshTableData();
                                    resetFormAddPhotoGallery();
                                    setShowAddPhotoGallery(false);
                                    setSelectedPhotoName("");
                                    notify.success(
                                      "Gallery image added successfully"
                                    );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Add Gallery Image
                            </Button>
                            <Button
                              onClick={() => {
                                setShowAddPhotoGallery(false);
                                setSelectedPhotoName("");
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      )}

                      <ConfigDynamicTable<any>
                        columns={columnGallery}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Gallery`}
                        tableName={photoGallerySchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },

                {
                  key: "6",
                  label: <span className={tabLabelClassName}>Committee</span>,
                  children: (
                    <>
                      {!showAddMember && (
                        <SchemaFormAutoSubmit
                          form={formCommitteeLeadership}
                          schemaName={committeeLeadershipSchema || ""}
                          recordId={
                            isEditing &&
                            editingSchema === committeeLeadershipSchema
                              ? editingRecordId
                              : ""
                          }
                          ref={formCommitteeLeadershipRef}
                        />
                      )}
                      {!showAddMember && (
                        <div className="flex justify-end gap-4">
                          {isEditing &&
                          editingSchema === committeeLeadershipSchema ? (
                            <>
                              <Button
                                type="primary"
                                onClick={async () => {
                                  try {
                                    const formValues =
                                      formCommitteeLeadership.values;
                                    const jdid = formValues.jdid;

                                    if (id === jdid) {
                                      await formCommitteeLeadershipRef.current?.handleSave();
                                      handleCancelEdit();
                                      refreshTableData();
                                      resetFormCommitteeLeadership();
                                      // notify.success(
                                      //   "Committee member updated successfully"
                                      // );
                                    } else {
                                      notify.error(
                                        "Chapter ID mismatch! Please check the ID field."
                                      );
                                    }
                                  } catch (error) {
                                    console.error("Save error:", error);
                                    // Validation error message is already shown by SchemaFormAutoSubmit
                                  }
                                }}
                              >
                                Update Committee Member
                              </Button>
                              <Button onClick={handleCancelEdit}>Cancel</Button>
                            </>
                          ) : (
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues =
                                    formCommitteeLeadership.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formCommitteeLeadershipRef.current?.handleSave();
                                    refreshTableData();
                                    resetFormCommitteeLeadership();
                                    // notify.success(
                                    //   "Committee member saved successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Save Committee & Leadership
                            </Button>
                          )}
                        </div>
                      )}
                      {showAddMember && selectedCommittee && (
                        <>
                          <div className="mb-4">
                            <div className="mb-4 flex items-center gap-4">
                              <Button
                                type="text"
                                icon={<ArrowLeft size={20} color="#666" />}
                                onClick={() => {
                                  setShowAddMember(false);
                                  setSelectedCommittee(null);
                                }}
                                style={{ color: "#666" }}
                              />
                              <h3
                                style={{
                                  margin: 0,
                                  fontSize: 18,
                                  fontWeight: 600,
                                  flex: 1,
                                }}
                              >
                                {selectedCommittee.committeename}
                              </h3>
                              <div className="flex justify-end gap-4">
                                <Button
                                  type="primary"
                                  onClick={() => {
                                    // Reset form before opening modal
                                    formAddCommitteeMember.reset();
                                    formAddCommitteeMember.clearErrors();
                                    formAddCommitteeMember.clearFormGraph();

                                    // Set initial values for new committee member
                                    formAddCommitteeMember.setInitialValues({
                                      jdid: id,
                                      cid: selectedCommittee.id,
                                    });
                                    formAddCommitteeMember.setValues({
                                      jdid: id,
                                      cid: selectedCommittee.id,
                                    });

                                    setShowAddMemberModal(true);
                                  }}
                                >
                                  Add Member
                                </Button>
                              </div>
                            </div>
                          </div>

                          <ConfigDynamicTable<any>
                            columns={columnCommitteeMembers}
                            key={refreshKey}
                            selectedRowKeys={selectedRowKeys}
                            setSelectedRowKeys={setSelectedRowKeys}
                            rowKey="id"
                            headerTitle={`Committee Members`}
                            tableName={addcommitteeMember || ""}
                            filter={{
                              cid: selectedCommittee.id,
                            }}
                          />
                        </>
                      )}

                      {/* Add Member Modal */}
                      <CommonModal
                        open={showAddMemberModal}
                        onCancel={() => {
                          // Complete cleanup
                          formAddCommitteeMember.reset();
                          formAddCommitteeMember.clearErrors();
                          formAddCommitteeMember.clearFormGraph();
                          // Clear cid value
                          if (selectedCommittee) {
                            formAddCommitteeMember.setInitialValues({});
                            formAddCommitteeMember.setValues({});
                          }
                          setShowAddMemberModal(false);
                        }}
                        title="Add Committee Member"
                        width={800}
                        footer={[
                          <Button
                            key="cancel"
                            onClick={() => {
                              // Complete cleanup
                              formAddCommitteeMember.reset();
                              formAddCommitteeMember.clearErrors();
                              formAddCommitteeMember.clearFormGraph();
                              // Clear cid value
                              if (selectedCommittee) {
                                formAddCommitteeMember.setInitialValues({});
                                formAddCommitteeMember.setValues({});
                              }
                              setShowAddMemberModal(false);
                            }}
                          >
                            Cancel
                          </Button>,
                          <Button
                            key="save"
                            type="primary"
                            onClick={handleAddMemberModalSave}
                          >
                            Save Member
                          </Button>,
                        ]}
                      >
                        <SchemaFormAutoSubmit
                          key="add-committee-member-form"
                          form={formAddCommitteeMember}
                          schemaName={addcommitteeMember || ""}
                          recordId=""
                          ref={formAddCommitteeMemberRef}
                        />
                      </CommonModal>
                      {!showAddMember && (
                        <ConfigDynamicTable<any>
                          columns={columnCommittee}
                          key={refreshKey}
                          selectedRowKeys={selectedRowKeys}
                          setSelectedRowKeys={setSelectedRowKeys}
                          rowKey="id"
                          headerTitle={`Committee & Leadership`}
                          tableName={committeeLeadershipSchema || ""}
                          filter={{
                            jdid: id,
                          }}
                        />
                      )}
                    </>
                  ),
                },

                {
                  key: "4",
                  label: <span className={tabLabelClassName}>Notice</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formNoticeBoard}
                        schemaName={noticeBoardSchema || ""}
                        recordId={
                          isEditing && editingSchema === noticeBoardSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formNoticeBoardRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === noticeBoardSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formNoticeBoard.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formNoticeBoardRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormNoticeBoard();
                                    // notify.success(
                                    //   "Notice updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Notice
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formNoticeBoard.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formNoticeBoardRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormNoticeBoard();
                                  // notify.success("Notice saved successfully");
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Notice
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsnoticeboard}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Notices`}
                        tableName={noticeBoardSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
                {
                  key: "10",
                  label: <span className={tabLabelClassName}>Banking</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formBanking}
                        schemaName={bankingSchema || ""}
                        recordId={
                          isEditing && editingSchema === bankingSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formBankingRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === bankingSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formBanking.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formBankingRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormBanking();
                                    // notify.success(
                                    //   "Banking information updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Banking
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formBanking.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formBankingRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormBanking();
                                  // notify.success(
                                  //   "Banking information saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Banking
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsbanking}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Payment Gateway`}
                        tableName={bankingSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
                {
                  key: "7",
                  label: <span className={tabLabelClassName}>Documents</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formDocuments}
                        schemaName={documentsSchema || ""}
                        recordId={
                          isEditing && editingSchema === documentsSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formDocumentsRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === documentsSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formDocuments.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formDocumentsRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormDocuments();
                                    // notify.success(
                                    //   "Document updated successfully"
                                    // );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Document
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formDocuments.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formDocumentsRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormDocuments();
                                  // notify.success("Document saved successfully");
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Document
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columndocuments}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Documents`}
                        tableName={documentsSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },

                {
                  key: "11",
                  label: <span className={tabLabelClassName}>FAQ</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formFAQ}
                        schemaName={faqSchema || ""}
                        recordId={
                          isEditing && editingSchema === faqSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formFAQRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === faqSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formFAQ.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formFAQRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormFAQ();
                                    // notify.success("FAQ updated successfully");
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update FAQ
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formFAQ.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formFAQRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormFAQ();
                                  // notify.success("FAQ saved successfully");
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save FAQ
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsFAQ}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`FAQ`}
                        tableName={faqSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
                {
                  key: "12",
                  label: <span className={tabLabelClassName}>Sponsorship</span>,
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formSponsorship}
                        schemaName={sponsorshipSchema || ""}
                        recordId={
                          isEditing && editingSchema === sponsorshipSchema
                            ? editingRecordId
                            : ""
                        }
                        ref={formSponsorshipRef}
                      />
                      <div className="flex justify-end gap-4">
                        {isEditing && editingSchema === sponsorshipSchema ? (
                          <>
                            <Button
                              type="primary"
                              onClick={async () => {
                                try {
                                  const formValues = formSponsorship.values;
                                  const jdid = formValues.jdid;

                                  if (id === jdid) {
                                    await formSponsorshipRef.current?.handleSave();
                                    handleCancelEdit();
                                    refreshTableData();
                                    resetFormSponsorship();
                                    notify.success(
                                      "Sponsorship updated successfully"
                                    );
                                  } else {
                                    notify.error(
                                      "Chapter ID mismatch! Please check the ID field."
                                    );
                                  }
                                } catch (error) {
                                  console.error("Save error:", error);
                                  // Validation error message is already shown by SchemaFormAutoSubmit
                                }
                              }}
                            >
                              Update Sponsorship
                            </Button>
                            <Button onClick={handleCancelEdit}>Cancel</Button>
                          </>
                        ) : (
                          <Button
                            type="primary"
                            onClick={async () => {
                              try {
                                const formValues = formSponsorship.values;
                                const jdid = formValues.jdid;

                                if (id === jdid) {
                                  await formSponsorshipRef.current?.handleSave();
                                  refreshTableData();
                                  resetFormSponsorship();
                                  // notify.success(
                                  //   "Sponsorship saved successfully"
                                  // );
                                } else {
                                  notify.error(
                                    "Chapter ID mismatch! Please check the ID field."
                                  );
                                }
                              } catch (error) {
                                console.error("Save error:", error);
                                // Validation error message is already shown by SchemaFormAutoSubmit
                              }
                            }}
                          >
                            Save Sponsorship
                          </Button>
                        )}
                      </div>
                      <ConfigDynamicTable<any>
                        columns={columnsSponsorship}
                        key={refreshKey}
                        selectedRowKeys={selectedRowKeys}
                        setSelectedRowKeys={setSelectedRowKeys}
                        rowKey="id"
                        headerTitle={`Sponsorships`}
                        tableName={sponsorshipSchema || ""}
                        filter={{
                          jdid: id,
                        }}
                      />
                    </>
                  ),
                },
              ]
            : [
                {
                  key: "1",
                  label: (
                    <span className={tabLabelClassName}>Basic Information</span>
                  ),
                  children: (
                    <>
                      <SchemaFormAutoSubmit
                        form={formOverview}
                        schemaName={basicInfoSchema ?? ""}
                        ref={formOverviewRef}
                      />
                      <div className="flex gap-4">
                        <Button type="primary" onClick={handleSaveOrganization}>
                          Save
                        </Button>
                      </div>
                    </>
                  ),
                },
              ]
        }
      />
    </>
  );
};

export default ChapterProfilePage;
